import { useCallback, useMemo, useState } from 'react'
import { type Resolver, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import {
  ONBOARDING_FIELDS_FOR_STEP,
  ONBOARDING_STEP_COUNT,
  type OnboardingFormValues,
  type OnboardingStepIndex
} from '../types'

/**
 * Yup schema for the full onboarding payload. `postalCode` and `logo` are
 * optional (default to '') so the form can be submitted from step 3 without
 * either filled in. Address fields are required as a group on step 2.
 */
export const buildOnboardingSchema = (t: (key: string) => string): yup.ObjectSchema<OnboardingFormValues> =>
  yup
    .object({
      name: yup.string().trim().required(t('org.onboarding.errors.nameRequired')).max(120),
      street: yup.string().trim().required(t('org.onboarding.errors.streetRequired')).max(120),
      city: yup.string().trim().required(t('org.onboarding.errors.cityRequired')).max(120),
      country: yup.string().trim().required(t('org.onboarding.errors.countryRequired')).max(120),
      postalCode: yup.string().trim().max(20).default(''),
      logo: yup.string().trim().max(500).default('')
    })
    .required()

export interface UseOrgOnboardingWizardResult {
  form: ReturnType<typeof useForm<OnboardingFormValues>>
  step: OnboardingStepIndex
  goNext: () => Promise<boolean>
  goBack: () => void
  goToStep: (target: OnboardingStepIndex) => void
  isFirstStep: boolean
  isLastStep: boolean
}

/**
 * Encapsulates wizard form state + step navigation. Step transitions on `goNext`
 * run react-hook-form validation for the CURRENT step's fields only (so users
 * can move forward without having seen later steps); final submit is owned by
 * the page (which calls `form.handleSubmit(...)` against the full schema).
 *
 * Kept intentionally framework-light — no Firestore reads, no mutations. Page
 * passes in nothing; the org-detail loading + guards live in the page.
 */
export const useOrgOnboardingWizard = (): UseOrgOnboardingWizardResult => {
  const { t } = useTranslation()
  const validationSchema = useMemo(() => buildOnboardingSchema(t as unknown as (k: string) => string), [t])
  const resolver = useMemo(
    () => yupResolver(validationSchema as never) as unknown as Resolver<OnboardingFormValues>,
    [validationSchema]
  )

  const form = useForm<OnboardingFormValues>({
    resolver,
    defaultValues: {
      name: '',
      street: '',
      city: '',
      country: '',
      postalCode: '',
      logo: ''
    },
    mode: 'onSubmit'
  })

  const [step, setStep] = useState<OnboardingStepIndex>(0)

  const goNext = useCallback(async (): Promise<boolean> => {
    const fields = ONBOARDING_FIELDS_FOR_STEP[step]
    const ok = await form.trigger(fields)
    if (!ok) return false
    if (step < ONBOARDING_STEP_COUNT - 1) {
      setStep((prev) => (prev + 1) as OnboardingStepIndex)
    }
    return true
  }, [form, step])

  const goBack = useCallback(() => {
    setStep((prev) => (prev > 0 ? ((prev - 1) as OnboardingStepIndex) : prev))
  }, [])

  const goToStep = useCallback((target: OnboardingStepIndex) => {
    if (target < 0 || target >= ONBOARDING_STEP_COUNT) return
    setStep(target)
  }, [])

  return {
    form,
    step,
    goNext,
    goBack,
    goToStep,
    isFirstStep: step === 0,
    isLastStep: step === ONBOARDING_STEP_COUNT - 1
  }
}
